/**
 * ADR-0233 — pure helpers for the ops2026 Experiments view.
 *
 * Statistics (Wilson 95% CI, two-proportion z-test p-value, Beta(1,1)
 * Monte-Carlo "probability variant beats control"), the "clear winner"
 * decision rule, and small formatters. Pure functions — tested in
 * `__tests__/experiments-helpers.test.ts`.
 */

import type {
  HomeExperimentFunnelCounts,
  HomeExperimentFunnelRow,
  HomeExperimentStatus,
  HomeExperimentThresholds,
  HomeExperimentWeights,
  HomeVariant,
} from '@sill/types/api';

export const VARIANTS: readonly HomeVariant[] = ['a', 'b', 'c'];

export const VARIANT_LABEL: Readonly<Record<HomeVariant, string>> = {
  a: 'A — Live Wire',
  b: 'B — Threshold',
  c: 'C — Open for Agents',
};

export const VARIANT_SHORT: Readonly<Record<HomeVariant, string>> = {
  a: 'A',
  b: 'B',
  c: 'C',
};

export const STATUS_LABEL: Readonly<Record<HomeExperimentStatus, string>> = {
  running: 'Running',
  paused: 'Paused',
  winner_declared: 'Winner declared',
};

/** Ordered funnel steps for the display, longest to shortest. The ADR
 *  specifies six events; the "views → scan submitted → scan completed
 *  → email submitted → signed up" display order matches. */
export const FUNNEL_STEPS: readonly (keyof HomeExperimentFunnelCounts)[] = [
  'view',
  'cta_click',
  'scan_submit',
  'scan_complete',
  'email_submit',
  'signup_created',
];

export const STEP_LABEL: Readonly<Record<keyof HomeExperimentFunnelCounts, string>> = {
  view: 'Views',
  cta_click: 'CTA clicks',
  scan_submit: 'Scan submitted',
  scan_complete: 'Scan completed',
  email_submit: 'Email submitted',
  signup_created: 'Signed up',
};

/** Default thresholds mirror the ADR §UI (`posterior ≥ 0.95`,
 *  `p ≤ 0.05`, `≥ 100 signups per arm`). The server echoes a
 *  config-level copy that may override these; the UI uses the
 *  server's thresholds and falls back to this when absent. */
export const DEFAULT_THRESHOLDS: HomeExperimentThresholds = {
  posterior: 0.95,
  p_value: 0.05,
  min_signups_per_arm: 100,
};

/**
 * Wilson 95% confidence interval for a proportion of successes in n
 * trials. Returns `[lo, hi]` clamped to `[0, 1]`. When n = 0 the
 * interval collapses to the full `[0, 1]` range — explicitly honest
 * about "we don't know".
 */
export function wilson95(successes: number, n: number): readonly [number, number] {
  if (n <= 0) return [0, 1];
  const z = 1.96;
  const p = successes / n;
  const denom = 1 + (z * z) / n;
  const centre = p + (z * z) / (2 * n);
  const margin = z * Math.sqrt((p * (1 - p) + (z * z) / (4 * n)) / n);
  const lo = Math.max(0, (centre - margin) / denom);
  const hi = Math.min(1, (centre + margin) / denom);
  return [lo, hi];
}

/**
 * Two-tailed p-value from a two-proportion z-test (null: p1 = p2).
 * Returns 1 when both arms are empty — "no evidence of a difference"
 * — rather than NaN. Uses a tight approximation to the standard
 * normal CDF; good to ~1e-7 absolute error, which is finer than any
 * decision this UI makes.
 */
export function twoProportionP(
  s1: number,
  n1: number,
  s2: number,
  n2: number,
): number {
  if (n1 <= 0 || n2 <= 0) return 1;
  const p1 = s1 / n1;
  const p2 = s2 / n2;
  const pooled = (s1 + s2) / (n1 + n2);
  const se = Math.sqrt(pooled * (1 - pooled) * (1 / n1 + 1 / n2));
  if (!Number.isFinite(se) || se === 0) return 1;
  const z = (p1 - p2) / se;
  // Two-tailed → 2 * (1 - Phi(|z|)).
  return 2 * (1 - standardNormalCdf(Math.abs(z)));
}

/** Approximate standard-normal CDF via Abramowitz & Stegun 26.2.17. */
function standardNormalCdf(x: number): number {
  // Phi(x) = 1 - phi(x) * (b1 t + b2 t^2 + b3 t^3 + b4 t^4 + b5 t^5)
  // for x >= 0, with t = 1 / (1 + 0.2316419 x). Mirror for negative x.
  if (!Number.isFinite(x)) return x > 0 ? 1 : 0;
  const sign = x < 0 ? -1 : 1;
  const a = Math.abs(x);
  const t = 1 / (1 + 0.2316419 * a);
  const phi = Math.exp(-(a * a) / 2) / Math.sqrt(2 * Math.PI);
  const poly =
    0.319381530 * t
    - 0.356563782 * (t * t)
    + 1.781477937 * (t * t * t)
    - 1.821255978 * (t * t * t * t)
    + 1.330274429 * (t * t * t * t * t);
  const upperTail = phi * poly;
  const cdf = 1 - upperTail;
  return sign > 0 ? cdf : 1 - cdf;
}

/**
 * Beta(1,1)-prior Bayesian "probability variant beats control" via
 * closed-form Monte Carlo. The posterior for each arm is
 * `Beta(successes + 1, failures + 1)`; we sample both and estimate
 * `P(p_variant > p_control)`.
 *
 * `rng` is injectable so tests get a deterministic answer. The
 * default is `Math.random`; 20 000 draws keeps the result within
 * ~0.005 of the true value at the thresholds that matter.
 */
export function probabilityBeatsControl(
  variantSuccesses: number,
  variantN: number,
  controlSuccesses: number,
  controlN: number,
  draws = 20_000,
  rng: () => number = Math.random,
): number {
  if (variantN <= 0 || controlN <= 0) return 0;
  let wins = 0;
  const aV = variantSuccesses + 1;
  const bV = variantN - variantSuccesses + 1;
  const aC = controlSuccesses + 1;
  const bC = controlN - controlSuccesses + 1;
  for (let i = 0; i < draws; i++) {
    const pV = betaSample(aV, bV, rng);
    const pC = betaSample(aC, bC, rng);
    if (pV > pC) wins++;
  }
  return wins / draws;
}

/** Beta(a, b) sample via two gamma draws: X/(X+Y), X~Gamma(a), Y~Gamma(b). */
function betaSample(a: number, b: number, rng: () => number): number {
  const x = gammaSample(a, rng);
  const y = gammaSample(b, rng);
  return x / (x + y);
}

/** Marsaglia–Tsang gamma sample (shape ≥ 1); boost shape < 1 via the
 *  standard shape+1 trick. Scale = 1. */
function gammaSample(shape: number, rng: () => number): number {
  if (shape < 1) {
    // Shape < 1: X = Y * U^(1/shape), Y ~ Gamma(shape+1).
    const y = gammaSample(shape + 1, rng);
    const u = Math.max(rng(), Number.EPSILON);
    return y * Math.pow(u, 1 / shape);
  }
  const d = shape - 1 / 3;
  const c = 1 / Math.sqrt(9 * d);
  for (;;) {
    let x: number;
    let v: number;
    do {
      x = standardNormal(rng);
      v = 1 + c * x;
    } while (v <= 0);
    const vCubed = v * v * v;
    const u = Math.max(rng(), Number.EPSILON);
    if (u < 1 - 0.0331 * (x * x) * (x * x)) return d * vCubed;
    if (Math.log(u) < 0.5 * x * x + d * (1 - vCubed + Math.log(vCubed))) return d * vCubed;
  }
}

/** Box–Muller standard normal. */
function standardNormal(rng: () => number): number {
  const u = Math.max(rng(), Number.EPSILON);
  const v = Math.max(rng(), Number.EPSILON);
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/**
 * "Clear winner" decision rule from the ADR:
 *   posterior ≥ thresholds.posterior
 *   AND p ≤ thresholds.p_value
 *   AND variant.signup_created ≥ thresholds.min_signups_per_arm
 *   AND control.signup_created ≥ thresholds.min_signups_per_arm
 *   AND variant conversion >= control conversion
 *
 * The last clause is a safety guard — "winner" only applies to arms
 * that are at least as good as the control; a stat-significant *loss*
 * is not a winner.
 */
export interface WinnerInputs {
  readonly variant: HomeVariant;
  readonly posterior: number;
  readonly p_value: number;
  readonly variant_signups: number;
  readonly control_signups: number;
  readonly variant_conversion: number;
  readonly control_conversion: number;
  readonly thresholds: HomeExperimentThresholds;
}

export function isClearWinner(inputs: WinnerInputs): boolean {
  const { thresholds } = inputs;
  if (inputs.variant_signups < thresholds.min_signups_per_arm) return false;
  if (inputs.control_signups < thresholds.min_signups_per_arm) return false;
  if (inputs.variant_conversion < inputs.control_conversion) return false;
  if (inputs.p_value > thresholds.p_value) return false;
  if (inputs.posterior < thresholds.posterior) return false;
  return true;
}

/** Validation for a weights edit: every value a non-negative integer in
 *  [0, 100] and the three sum to exactly 100. Returns null when valid,
 *  else a human-readable reason. */
export function validateWeights(w: HomeExperimentWeights): string | null {
  for (const v of VARIANTS) {
    const n = w[v];
    if (!Number.isInteger(n)) return `Weight ${v.toUpperCase()} must be a whole number.`;
    if (n < 0 || n > 100) return `Weight ${v.toUpperCase()} must be between 0 and 100.`;
  }
  const sum = w.a + w.b + w.c;
  if (sum !== 100) return `Weights must sum to 100 (currently ${sum}).`;
  return null;
}

/** Rate (0..1) of a step relative to views, with a 0 denominator → 0. */
export function conversionRate(row: HomeExperimentFunnelRow, step: keyof HomeExperimentFunnelCounts): number {
  if (row.view <= 0) return 0;
  return row[step] / row.view;
}

/** Signed-up / Views for a row. The primary "wins" metric for the
 *  decision rule. */
export function signupConversion(row: HomeExperimentFunnelRow): number {
  return conversionRate(row, 'signup_created');
}

export function findRow(
  rows: readonly HomeExperimentFunnelRow[],
  variant: HomeVariant,
): HomeExperimentFunnelRow | null {
  return rows.find((r) => r.variant === variant) ?? null;
}

/** Format a rate (0..1) as a percentage with 2 decimals. "—" for null. */
export function formatRatePct(r: number | null | undefined): string {
  if (r === null || r === undefined || !Number.isFinite(r)) return '—';
  const pct = r * 100;
  if (pct <= 0) return '0%';
  if (pct < 0.01) return '<0.01%';
  if (pct >= 100) return '100%';
  return `${pct.toFixed(2)}%`;
}

/** Format a p-value: "< 0.001" for very small, "0.XXX" otherwise. */
export function formatPValue(p: number | null | undefined): string {
  if (p === null || p === undefined || !Number.isFinite(p)) return '—';
  if (p < 0.001) return '<0.001';
  return p.toFixed(3);
}

/** Format a posterior probability as a percentage (0 decimals). */
export function formatPosterior(p: number | null | undefined): string {
  if (p === null || p === undefined || !Number.isFinite(p)) return '—';
  return `${Math.round(p * 100)}%`;
}
